import { useId, useState } from 'react';

import { Button, Dialog, Field, Input, Spinner } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { KHOA_LOI_TEN } from '../constants';
import { useChiTietPhongBan } from '../hooks/usePhongBan';
import { DAI_TEN_TOI_DA, DO_SAU_TOI_DA, kiemTenPhongBan, loiTheoConTro } from '../nghiep-vu/cay';
import { CayChonPhongBan } from './CayChonPhongBan';
import type { Department, DepartmentCreate, DepartmentUpdate } from '../types';

/** Tạo mới (có thể đặt sẵn cha) hoặc sửa một phòng ban có sẵn. */
export type CheDoForm = { loai: 'tao'; cha: Department | null } | { loai: 'sua'; phongBan: Department };

/** Lỗi của server đã nằm dưới một ô thì không lặp lại ở dải chung. */
const O_TREN_FORM = ['/name', '/parent_id'];

/**
 * Hộp thoại thêm hoặc sửa phòng ban (`POST /api/v1/departments`,
 * `PUT /api/v1/departments/{id}`).
 *
 * Sửa: đọc phòng ban (kèm `ETag`) khi mở hộp và gửi lại ETag đó ở `If-Match`.
 * Người khác vừa sửa thì server trả `412`; hook đọc lại bản mới, hộp giữ chữ
 * người dùng đang gõ và báo để họ lưu lại.
 *
 * Ô phòng ban cha bỏ chính phòng ban đang sửa và con cháu của nó (server sẽ trả
 * `422 DEPARTMENT_CYCLE`), và khoá những phòng ban mà đặt vào thì cây vượt
 * `DO_SAU_TOI_DA` cấp.
 */
export function FormPhongBan({
  cheDo,
  dsPhongBan,
  open,
  dangLuu,
  onClose,
  onTao,
  onSua,
}: {
  cheDo: CheDoForm;
  /** Cả cây hiện có, để dựng ô chọn cha. */
  dsPhongBan: readonly Department[];
  open: boolean;
  dangLuu: boolean;
  onClose: () => void;
  onTao: (du: DepartmentCreate) => Promise<unknown>;
  onSua: (id: string, du: DepartmentUpdate, etag: string) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const dangSua = cheDo.loai === 'sua' ? cheDo.phongBan : null;
  const chiTiet = useChiTietPhongBan(dangSua?.id ?? null);

  const [ten, setTen] = useState(dangSua?.name ?? '');
  const [cha, setCha] = useState<string | null>(
    cheDo.loai === 'sua' ? cheDo.phongBan.parent_id : (cheDo.cha?.id ?? null),
  );
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: ApiError; truong: Record<string, string> } | null>(null);

  const maTen = kiemTenPhongBan(ten);
  const loiTen =
    (maTen && (daGui || maTen === 'dai') ? t(KHOA_LOI_TEN[maTen], { so: DAI_TEN_TOI_DA }) : undefined) ??
    loiServer?.truong['/name'];
  const loiCha = loiServer?.truong['/parent_id'];
  const loiChung =
    loiServer && !O_TREN_FORM.some((o) => o in loiServer.truong) ? loiServer.loi : null;

  const banGoc = chiTiet.data;
  const coThayDoi = dangSua === null || ten.trim() !== dangSua.name || cha !== dangSua.parent_id;
  const choChiTiet = dangSua !== null && !banGoc;

  const gui = async () => {
    setDaGui(true);
    if (maTen || !coThayDoi || dangLuu || choChiTiet) return;
    setLoiServer(null);
    try {
      if (dangSua && banGoc) {
        await onSua(dangSua.id, { name: ten.trim(), parent_id: cha }, banGoc.etag);
      } else {
        await onTao({ name: ten.trim(), parent_id: cha });
      }
    } catch (err) {
      const e = err as ApiError;
      setLoiServer({ loi: e, truong: loiTheoConTro(e.details) });
    }
  };

  const tieuDe =
    cheDo.loai === 'sua'
      ? t('coCauToChuc.suaTieuDe', { ten: cheDo.phongBan.name })
      : cheDo.cha
        ? t('coCauToChuc.taoConTieuDe', { ten: cheDo.cha.name })
        : t('coCauToChuc.taoTieuDe');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={tieuDe}
      width={520}
      destroyOnHidden
      footer={
        // Chân hộp của antd căn phải; dải lỗi nhiều dòng phải đọc từ trái.
        <div className="space-y-3 text-left">
          {loiChung && (
            <LoiTrongHop>
              {loiChung.status === 412
                ? t('coCauToChuc.xungDot', { ten: banGoc?.name ?? dangSua?.name ?? '' })
                : dichLoi(loiChung)}
            </LoiTrongHop>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              htmlType="submit"
              form={formId}
              loading={dangLuu}
              disabled={!coThayDoi || choChiTiet}
            >
              {dangLuu
                ? t('chung.dangLuu')
                : dangSua
                  ? t('coCauToChuc.luuSua')
                  : t('coCauToChuc.luuTao')}
            </Button>
          </div>
        </div>
      }
    >
      {dangSua && chiTiet.error && !banGoc ? (
        <div className="space-y-3 pt-2">
          <LoiTrongHop>{t('coCauToChuc.loiChiTiet', { loi: dichLoi(chiTiet.error) })}</LoiTrongHop>
          <Button variant="outline" size="sm" onClick={() => void chiTiet.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <form
          id={formId}
          noValidate
          className="space-y-4 pt-2"
          onSubmit={(e) => {
            e.preventDefault();
            void gui();
          }}
        >
          {choChiTiet && (
            <div role="status" className="flex items-center gap-2 text-xs text-content-muted">
              <Spinner size="small" />
              {t('coCauToChuc.dangTaiChiTiet')}
            </div>
          )}
          <Field label={t('coCauToChuc.fTen')} required error={loiTen}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                maxLength={DAI_TEN_TOI_DA + 50}
                placeholder={t('coCauToChuc.fTenPlaceholder')}
                value={ten}
                onChange={(e) => {
                  setTen(e.target.value);
                  setLoiServer(null);
                }}
              />
            )}
          </Field>
          <Field
            label={t('coCauToChuc.fCha')}
            hint={t('coCauToChuc.fChaGoiY', { so: DO_SAU_TOI_DA })}
            error={loiCha}
          >
            {(p) => (
              <CayChonPhongBan
                fieldProps={p}
                ds={dsPhongBan}
                dangSua={dangSua?.id}
                khoaQuaSau
                placeholder={t('coCauToChuc.fChaTrong')}
                khongCo={t('coCauToChuc.fChaKhongCo')}
                value={cha}
                onChange={(v) => {
                  setCha(v);
                  setLoiServer(null);
                }}
              />
            )}
          </Field>
        </form>
      )}
    </Dialog>
  );
}
